<template>
  <div class="rpt-industry-operation">
    <div class="report-shell">
      <header class="report-header">
        <div class="header-row">
          <div>
            <h1>五大行业经营情况</h1>
            <p class="subtitle">
              从行业景气度、客户投资、签约达成和丢单原因四个维度，对家电、汽车、泛半导体、机械装备和流程行业进行横向经营评价。
            </p>
          </div>
          <div class="header-actions">
            <span class="report-badge"
              ><i class="fa fa-clock-o"></i> 更新至 2026-07-24</span
            >
            <!-- <button type="button" class="analysis-button" @click="requestAnalysis">
              <i class="fa fa-magic"></i><span>智能分析</span>
            </button> -->
          </div>
        </div>
      </header>

      <section class="kpi-grid">
        <article v-for="(kpi, i) in kpis" :key="i" class="kpi-card">
          <div class="kpi-label">{{ kpi.label }}</div>
          <div class="kpi-value" :style="{ color: toneColor(kpi.tone) }">
            {{ kpi.value }}
          </div>
          <div class="kpi-note">{{ kpi.note }}</div>
        </article>
      </section>

      <section class="table-card">
        <div class="table-card-head">
          <div>
            <div class="section-title">
              <i class="fa fa-industry"></i><span>五大行业经营对标及丢单分析</span>
            </div>
            <div class="section-note">
              金额单位：万元；完成率 = H1 签约额 ÷ H1
              目标签约额；行业与链群关系采用当前经营管理归属。
            </div>
          </div>
          <div class="legend">
            <span
              ><i class="legend-dot" style="background: #0aa66a"></i>完成率 ≥ 100%</span
            >
            <span
              ><i class="legend-dot" style="background: #4a5ff3"></i>90% ≤ 完成率 &lt;
              100%</span
            >
            <span
              ><i class="legend-dot" style="background: #e54b3d"></i>完成率 &lt; 90%</span
            >
          </div>
        </div>
        <div class="table-scroll">
          <table>
            <thead>
              <tr>
                <th rowspan="2" class="col-name">五大行业</th>
                <th rowspan="2" class="col-chain">对应链群</th>
                <th colspan="2">跟自己比</th>
                <th colspan="2">丢单分析</th>
                <th rowspan="2" class="col-reason">丢单原因</th>
                <th rowspan="2" class="col-eval">评价</th>
              </tr>
              <tr>
                <th>签约额</th>
                <th>签约完成率</th>
                <th>丢单金额</th>
                <th>占比</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in rows" :key="i">
                <td class="industry-name">{{ row.industry }}</td>
                <td>
                  <span class="chain-list">{{ row.chains }}</span>
                </td>
                <td class="numeric">{{ num(row.signed) }}</td>
                <td class="rate-cell">
                  <span
                    class="rate-fill"
                    :style="{
                      width: Math.min(completion(row), 100).toFixed(1) + '%',
                      background: statusColor(row),
                    }"
                  ></span>
                  <span class="rate-text" :style="{ color: statusColor(row) }"
                    >{{ completion(row).toFixed(1) }}%</span
                  >
                </td>
                <td class="numeric">{{ num(row.lost) }}</td>
                <td class="numeric">{{ (row.lostRate * 100).toFixed(2) }}%</td>
                <td class="text-left">{{ row.reason }}</td>
                <td>
                  <div class="evaluation">
                    <i class="status-light" :style="{ background: statusColor(row) }"></i
                    ><span :style="{ color: statusColor(row) }">{{
                      statusLabel(row)
                    }}</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <footer class="report-foot">
          <span>数据来源：销售漏斗、CRM 签约台账、重点客户商机复盘及行业研究周报。</span>
          <span
            >管理口径：评价灯仅依据签约完成率着色，不替代利润、回款和交付质量判断。</span
          >
        </footer>
      </section>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'IndustryOperationReport',
  data() {
    return {
      loading: false,
      apiData: null
    }
  },
  async created() {
    await this.fetchData()
  },
  computed: {
    rows() {
      if (!this.apiData) return []
      return this.apiData.map(function (d) {
        return {
          industry: d.industry || '—',
          chains: d.chain_group || '—',
          target: d.h1_target_signing_amount != null ? Number(d.h1_target_signing_amount) : 0,
          signed: d.h1_actual_signing_amount != null ? Number(d.h1_actual_signing_amount) : 0,
          lost: d.lost_order_amount != null ? Number(d.lost_order_amount) : 0,
          // lost_order_ratio 后端返回的是小数比例(如 0.67),展示时在模板处 *100 转为百分比(67.00%)
          lostRate: d.lost_order_ratio != null ? Number(d.lost_order_ratio) : 0,
          rate: d.signing_completion_rate != null ? Number(d.signing_completion_rate) : 0,
          reason: d.lost_order_reason || '—',
          evaluation: d.evaluation || '—'
        }
      })
    },
    totalTarget() {
      return this.rows.reduce(function (s, r) { return s + r.target }, 0)
    },
    totalSigned() {
      return this.rows.reduce(function (s, r) { return s + r.signed }, 0)
    },
    totalLost() {
      return this.rows.reduce(function (s, r) { return s + r.lost }, 0)
    },
    overallRate() {
      return this.totalTarget ? (this.totalSigned / this.totalTarget) * 100 : 0
    },
    riskCount() {
      return this.rows.filter(function (r) { return r.evaluation === '风险' }).length
    },
    kpis() {
      var diff = this.totalSigned - this.totalTarget
      return [
        {
          label: 'H1 目标签约额',
          value: this.num(this.totalTarget) + ' 万',
          note: '五大行业合计经营目标',
          tone: 'primary'
        },
        {
          label: 'H1 实际签约额',
          value: this.num(this.totalSigned) + ' 万',
          note: '较目标' + (diff >= 0 ? '超额 ' : '缺口 ') + this.num(Math.abs(diff)) + ' 万',
          tone: diff >= 0 ? 'positive' : 'negative'
        },
        {
          label: '整体签约完成率',
          value: this.overallRate.toFixed(2) + '%',
          note: '五大行业汇总口径',
          tone: this.overallRate >= 100 ? 'positive' : this.overallRate >= 90 ? 'warning' : 'negative'
        },
        {
          label: '丢单金额 / 风险行业',
          value: this.num(this.totalLost) + ' 万 / ' + this.riskCount + ' 个',
          note: '评价为风险的行业',
          tone: 'negative'
        }
      ]
    }
  },
  methods: {
    num(v) {
      return Math.round(v).toLocaleString('zh-CN')
    },
    toneColor(t) {
      const m = {
        primary: '#4a5ff3',
        positive: '#0aa66a',
        warning: '#d98213',
        negative: '#e54b3d'
      }
      return m[t] || '#4a5ff3'
    },
    completion(row) {
      return row.rate || (row.target ? (row.signed / row.target) * 100 : 0)
    },
    async fetchData() {
      this.loading = true
      try {
        var year = new Date().getFullYear().toString()
        var response = await axios.post(
          'https://bigdata.cosmoplat.com/prod-api/system/api/call/cosmoplat/five-industry/operation',
          { secret: '88py6X', paramList: [{ paramName: 'year', paramValue: year }] },
          { headers: { 'Content-Type': 'application/json' }}
        )
        this.apiData = (response.data && response.data.data) || []
      } catch (e) {
        this.apiData = []
      } finally {
        this.loading = false
      }
    },
    statusColor(row) {
      const m = {
        达标: '#0aa66a',
        需关注: '#e54b3d',
        风险: '#e54b3d',
        领先: '#0aa66a',
        基本达成: '#4a5ff3',
        承压: '#e54b3d'
      }
      return m[row.evaluation] || '#e54b3d'
    },
    statusLabel(row) {
      return (
        row.evaluation ||
        (() => {
          const r = this.completion(row)
          return r >= 100 ? '领先' : r >= 90 ? '基本达成' : '承压'
        })()
      )
    },
    requestAnalysis() {
      const ranked = [...this.rows].sort(
        (a, b) => this.completion(b) - this.completion(a)
      )
      const lostLeader = [...this.rows].sort((a, b) => b.lost - a.lost)[0]
      const riskRows = this.rows.filter((r) => this.completion(r) < 90)
      const risks = riskRows.map(
        (r) =>
          r.industry +
          '签约完成率为' +
          this.completion(r).toFixed(1) +
          '%，丢单' +
          this.num(r.lost) +
          '万元，主要受' +
          r.reason +
          '影响'
      )
      if (!risks.length) {
        risks.push(
          '当前没有完成率低于90%的红灯行业，但家电、泛半导体和流程行业仍存在目标缺口。'
        )
      }
      risks.push(
        lostLeader.industry +
          '丢单金额最高，为' +
          this.num(lostLeader.lost) +
          '万元，需优先复盘重点客户和报价策略。'
      )
      window.parent.postMessage(
        {
          type: 'cosmo:business-report-analysis',
          report: {
            key: 'industry-operation',
            title: '五大行业经营情况',
            summary: [
              '五大行业H1目标签约额' +
                this.num(this.totalTarget) +
                '万元，实际签约' +
                this.num(this.totalSigned) +
                '万元，整体完成率' +
                this.overallRate.toFixed(1) +
                '%。',
              ranked[0].industry +
                '完成率最高，为' +
                this.completion(ranked[0]).toFixed(1) +
                '%；' +
                ranked[ranked.length - 1].industry +
                '完成率最低，为' +
                this.completion(ranked[ranked.length - 1]).toFixed(1) +
                '%。',
              '累计丢单' +
                this.num(this.totalLost) +
                '万元，行业分化主要体现在客户投资节奏、价格竞争和项目审批周期。'
            ],
            risks,
            recommendations: [
              '对完成率低于90%的行业建立红灯专项清单，逐项明确重点客户、补差金额、责任链群和签约时间。',
              '针对高丢单行业复盘预算、价格、方案和决策周期，形成可复用的竞争策略和报价边界。',
              '将高景气行业的售前与交付资源前置，推动下半年高确定性商机尽早签约并锁定收入。'
            ],
            data: {
              target: this.totalTarget,
              signed: this.totalSigned,
              lost: this.totalLost,
              rows: this.rows
            }
          }
        },
        window.location.origin
      )
    }
  }
}
</script>

<style lang="scss">
.rpt-industry-operation {
  height: 100%;
  min-height: 0;
  padding: 16px 10px;
  background: #eef3f9;
  overflow-y: auto;
  font-family: "PingFang SC", "HarmonyOS Sans SC", "Microsoft YaHei", -apple-system,
    BlinkMacSystemFont, sans-serif;
  color: #0f172a;
  scrollbar-width: thin;
  scrollbar-color: #fff transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #fff;
    border-radius: 2px;
  }

  h1 {
    margin: 0 0 6px;
    font-size: 24px;
    line-height: 1.25;
    letter-spacing: -0.02em;
    color: #0f172a;
    font-weight: 800;
  }

  .report-shell {
    display: flex;
    height: 100%;
    max-width: 1600px;
    margin: 0 auto;
    flex-direction: column;
  }

  .report-header {
    position: relative;
    overflow: hidden;
    padding: 18px 22px;
    border: 1px solid rgba(148, 163, 184, 0.28);
    border-radius: 15px;
    background: linear-gradient(135deg, #ffffff, #f8fafc);
    box-shadow: 0 16px 38px rgba(15, 23, 42, 0.08);
    flex-shrink: 0;
    margin-bottom: 12px;
    &::after {
      content: "";
      position: absolute;
      right: -80px;
      top: -100px;
      width: 260px;
      height: 260px;
      border-radius: 50%;
      background: radial-gradient(
        circle,
        rgba(74, 95, 243, 0.18),
        rgba(74, 95, 243, 0) 68%
      );
      pointer-events: none;
    }
  }
  .header-row {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
  }
  .subtitle {
    margin: 0;
    color: #64748b;
    font-size: 13px;
    line-height: 1.5;
  }
  .header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }
  .report-badge {
    height: 34px;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 0 13px;
    border-radius: 9px;
    border: 1px solid rgba(148, 163, 184, 0.28);
    background: #fff;
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
  }
  .analysis-button {
    height: 34px;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 0 14px;
    border-radius: 9px;
    border: none;
    background: linear-gradient(135deg, #4a5ff3, #2d7ff9);
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    box-shadow: 0 7px 16px rgba(74, 95, 243, 0.2);
    &:hover {
      filter: brightness(1.08);
    }
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
    margin: 8px 0;
    flex-shrink: 0;
  }
  .kpi-card {
    position: relative;
    overflow: hidden;
    padding: 10px 12px;
    border: 1px solid rgba(148, 163, 184, 0.28);
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.045);
    &::after {
      content: "";
      position: absolute;
      right: -24px;
      bottom: -34px;
      width: 86px;
      height: 86px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(74, 95, 243, 0.1), transparent 70%);
      pointer-events: none;
    }
  }
  .kpi-label {
    font-size: 12px;
    color: #64748b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .kpi-value {
    margin-top: 4px;
    font-size: 20px;
    font-weight: 850;
    letter-spacing: -0.02em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .kpi-note {
    margin-top: 2px;
    font-size: 10px;
    color: #64748b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .table-card {
    display: flex;
    min-height: 0;
    flex: 1;
    overflow: hidden;
    border: 1px solid rgba(148, 163, 184, 0.28);
    border-radius: 15px;
    background: #fff;
    box-shadow: 0 16px 38px rgba(15, 23, 42, 0.08);
    flex-direction: column;
  }
  .table-card-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 16px;
    border-bottom: 1px solid rgba(148, 163, 184, 0.28);
    flex-shrink: 0;
  }
  .section-title {
    display: flex;
    align-items: center;
    gap: 9px;
    font-size: 14px;
    font-weight: 800;
    color: #0f172a;
  }
  .section-note {
    margin-top: 4px;
    font-size: 11px;
    color: #64748b;
  }
  .legend {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
    font-size: 11px;
    color: #64748b;
    flex-shrink: 0;
    span {
      display: inline-flex;
      align-items: center;
      gap: 5px;
    }
  }
  .legend-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  .table-scroll {
    min-height: 0;
    flex: 1;
    overflow: auto;
    table {
      width: 100%;
      min-width: 1000px;
      border-collapse: separate;
      border-spacing: 0;
      font-size: 11px;
      font-variant-numeric: tabular-nums;
    }
    thead {
      th {
        position: sticky;
        top: 0;
        z-index: 4;
        color: #fff;
        text-align: center;
        font-weight: 800;
        padding: 8px 6px;
        border-right: 1px solid rgba(148, 163, 184, 0.28);
        border-bottom: 1px solid rgba(148, 163, 184, 0.28);
        white-space: nowrap;
        background: #0d5fac;
        font-size: 11px;
      }
      th:first-child {
        z-index: 6;
      }
    }
    .col-name {
      width: 9%;
    }
    .col-chain {
      width: 12%;
    }
    .col-reason {
      width: 20%;
    }
    .col-eval {
      width: 7%;
    }
    th:first-child,
    td:first-child {
      position: sticky;
      left: 0;
    }
    tbody {
      td {
        padding: 8px 6px;
        border-right: 1px solid rgba(148, 163, 184, 0.28);
        border-bottom: 1px solid rgba(148, 163, 184, 0.28);
        text-align: center;
        white-space: nowrap;
      }
      td:first-child {
        z-index: 2;
        background: #fff;
        font-weight: 700;
      }
      tr:last-child td {
        border-bottom: 0;
      }
    }
    th:last-child,
    td:last-child {
      border-right: 0;
    }
  }
  .industry-name {
    font-weight: 800;
    text-align: left !important;
  }
  .chain-list {
    font-size: 10px;
    color: #64748b;
  }
  .text-left {
    text-align: left !important;
    white-space: normal !important;
    min-width: 160px;
  }
  .numeric {
    text-align: right !important;
  }
  .rate-cell {
    position: relative;
    overflow: hidden;
  }
  .rate-fill {
    position: absolute;
    inset: 3px auto 3px 3px;
    border-radius: 4px;
    opacity: 0.12;
    pointer-events: none;
  }
  .rate-text {
    position: relative;
    z-index: 1;
    font-weight: 800;
  }
  .evaluation {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    font-size: 11px;
    font-weight: 800;
  }
  .status-light {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
    flex-shrink: 0;
  }

  .report-foot {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 14px;
    border-top: 1px solid rgba(148, 163, 184, 0.28);
    font-size: 10px;
    color: #64748b;
    line-height: 1.5;
    flex-shrink: 0;
  }
}
</style>
